export default {
    methods: {
        /**
         * Allows retrieving value in nested properties of object using dot notation
         * Example:
         *
         * var someObj = { nested: { prop: 'some value', prop2: 'other value' }, prop: 'neki' }
         * dotGet('nested.prop', someObj) returns 'some value'
         *
         * @param {string} key
         * @param {object} obj
         */
        dotGet(key, obj) {
            if (!obj) {
                return null;
            }

            if (obj[key]) {
                return obj[key];
            }

            // split by first occurence of dot character ('.')
            let splitKey = key.split(/\.(.+)/);

            if (splitKey.length === 1) {
                return null;
            }

            let nestedKey = splitKey[1];
            let nestedObj = obj[splitKey[0]];

            return this.dotGet(nestedKey, nestedObj);
        },

        /**
         * Allows setting value to nested properties of object using dot notation
         *
         * Example:
         * var someObj = { nested: { prop: 'some value', prop2: 'other value' }, prop: 'neki' }
         * dotSet('nested.prop', someObj, 'now im different')
         * dotGet('nested.prop', someObj) returns 'now im different'
         *
         * @param {string} key
         * @param {object} obj
         * @param {any} val
         */
        dotSet(key, obj, val) {
            if (!obj) {
                return false;
            }

            if (obj[key]) {
                obj[key] = val;
                return true;
            }

            let splitKey = key.split(/\.(.+)/);

            if (splitKey.length === 1) {
                return false;
            }

            let nestedKey = splitKey[1];
            let nestedObj = obj[splitKey[0]];

            return this.dotSet(nestedKey, nestedObj, val);
        },

        /**
         * Shallow object copy. It basically copies properties of object to a new
         * object. If a property is an object, its' reference is copied
         *
         * @param {object} obj
         */
        objectCopy(obj) {
            let copy = {};

            for (let prop in obj) {
                copy[prop] = obj[prop];
            }

            return copy;
        },

        /**
         * Copies objB properties into objA
         * If objB property value is an object, its' reference gets
         * copied, ie objA.propB is the same as objB.propB
         *
         * @param {object} objA
         * @param {object} objB
         */
        objectSet(objA, objB) {
            for (let prop in objB) {
                objA[prop] = objB[prop];
            }
        },

        /**
         * Returns a copy of obj without properties specified in props
         *
         * @param {object} obj
         * @param {array<string>} props
         * @return {object}
         */
        objectWithout(obj, props) {
            let copy = this.objectCopy(obj);

            props.forEach(prop => {
                delete copy[prop];
            });

            return copy;
        },

        /**
         * Returns a copy of obj only with properties specified in props
         *
         * @param {object} obj
         * @param {array} props
         * @return {object}
         */
        objectOnly(obj, props) {
            let copy = this.objectCopy(obj);

            for (let prop in copy) {
                if (props.indexOf(prop) < 0) {
                    delete copy[prop];
                }
            }

            return copy;
        },

        /**
         * Merges two objects. If they have properties with the same name, the
         * value of obj2 is used.
         *
         * @param {Object} obj
         * @param {Object} obj2
         * @return {Object}
         */
        objectMerge(obj, obj2) {
            let result = Object.assign({}, obj);

            for (let prop in obj2) {
                if (typeof obj2[prop] === 'object') {
                    result[prop] = this.objectMerge(result[prop], obj2);
                } else {
                    result[prop] = obj2[prop];
                }
            }

            return result;
        },

        /**
         * Compares both objects own properties and their values to check for equality.
         *
         * @param {Object} obj1
         * @param {Object} obj2
         * @return {Boolean}
         */
        objectEqualsByValue(obj1, obj2) {
            for (let prop in obj1) {
                if (obj1[prop] !== obj2[prop]) {
                    // it will return false on first difference
                    return false;
                }
            }

            return true;
        },

        /**
         * Returns and object index in array, or -1 if not found. It compares objects
         * by value (see this.objectEqualsByValue()).
         *
         * @param {*} arr
         * @param {*} obj
         */
        objectFindInArray(arr, obj) {
            let foundIdx = -1;

            arr.forEach((item, idx) => {
                if (this.objectEqualsByValue(item, obj)) {
                    foundIdx = idx;
                }
            });

            return foundIdx;
        },

        /**
         * Replaces once object with another within array by reference and keeps reactivity
         *
         * @param {Array} arr // array in which there the object to replace with is
         * @param {Object} obj // object to be replaced (by reference)
         * @param {Object} replaceObj // object to replace the previous object with
         */
        spliceByObjReference(arr, obj, replaceObj) {
            let index = arr.indexOf(obj);

            if (index !== -1) {
                if (replaceObj) {
                    arr.splice(index, 1, replaceObj);
                } else {
                    arr.splice(index, 1);
                }
            }
        },
    },
};



// WEBPACK FOOTER //
// ./resources/assets/js/components/mixins/ObjectUtilsMixin.js